import React, { useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import PropTypes, { number } from 'prop-types';
import './advance-popover.scss';

function AdvancePopover({
  placement,
  getEvent,
  closePopover,
  reference,
  closeOnclick,
  className,
  popoverOpen,
  maskColor,
  zIndex,
  setWidth,
  setMinWidth,
  ...props
}) {
  const [leftPosition, setLeftPosition] = useState();
  const [topPosition, setTopPosition] = useState();
  const popoverContainer = useRef(null);

  useEffect(() => {
    if (!popoverContainer?.current) return;

    const el = popoverContainer.current;

    const observer = new ResizeObserver(() => {
      const popoverHeight = el.clientHeight;
      const popoverWidth = el.clientWidth;

      if (
        typeof getEvent === 'object' &&
        getEvent !== null &&
        'getBoundingClientRect' in getEvent
      ) {
        const rectangle = getEvent.getBoundingClientRect();

        let left = 0;
        let top = 0;

        switch (placement) {
          case 'self-bottom':
            left = rectangle.left + rectangle.width / 2 - popoverWidth / 2;
            top =
              rectangle.y + popoverHeight > window.innerHeight - 100
                ? rectangle.y - popoverHeight
                : rectangle.y + rectangle.height;
            break;

          case 'right':
            left = rectangle.x + getEvent.clientWidth + rectangle.width;
            top =
              rectangle.y + popoverHeight > window.innerHeight - 100
                ? rectangle.y - popoverHeight
                : rectangle.y + getEvent.clientHeight;
            break;

          case 'right-center': {
            left = rectangle.left + rectangle.width / 2 - popoverWidth / 2;
            top = rectangle.bottom;

            if (left + popoverWidth > window.innerWidth) {
              left = window.innerWidth - popoverWidth - 16;
            }
            if (left < 16) left = 16;
            if (top + popoverHeight > window.innerHeight - 20) {
              top = rectangle.top - popoverHeight;
            }
            break;
          }

          case 'top':
            left = rectangle.x;
            top = rectangle.y - popoverHeight;
            break;

          case 'top-right':
            left = rectangle.x + rectangle.width;
            top = rectangle.y - popoverHeight;
            break;

          case 'left':
            top =
              rectangle.y + popoverHeight > window.innerHeight - 100
                ? rectangle.y - popoverHeight
                : rectangle.y + rectangle.height;

            left = rectangle.x - popoverWidth + rectangle.width;
            break;

          case 'left-center':
            top =
              rectangle.y + popoverHeight > window.innerHeight - 100
                ? rectangle.y - popoverHeight
                : rectangle.y + rectangle.height;

            left = rectangle.x;
            break;

          case 'center':
            top =
              rectangle.y + popoverHeight > window.innerHeight - 100
                ? rectangle.y - popoverHeight
                : rectangle.y + getEvent.clientHeight;

            left = rectangle.x - getEvent.clientWidth / 2;
            break;

          case 'middle':
            left = rectangle.x + rectangle.width / 2 + popoverWidth / 2;
            top = rectangle.y;
            break;

          case 'top-left':
            left = rectangle.x - popoverWidth + rectangle.width;
            top = rectangle.y - popoverHeight;
            break;

          default:
            left =
              rectangle.x + popoverWidth > window.innerWidth
                ? rectangle.x - popoverWidth
                : rectangle.x;

            top =
              rectangle.y + popoverHeight > window.innerHeight - 100
                ? rectangle.y + rectangle.height - popoverHeight
                : rectangle.y;
        }

        setLeftPosition(left);
        setTopPosition(top);

        document.body.style.overflow = 'hidden';
        const wrapper = document.querySelector('.wrapper-right');
        if (wrapper) wrapper.style.overflow = 'hidden';
      } else {
        setLeftPosition(undefined);
        setTopPosition(undefined);

        document.body.style.overflow = 'auto';
        const wrapper = document.querySelector('.wrapper-right');
        if (wrapper) wrapper.style.overflow = 'auto';
      }
    });

    observer.observe(el);
  }, [getEvent, placement]);

  return ReactDOM.createPortal(
    <>
      <button
        type="button"
        className={`${
          leftPosition && topPosition && reference === getEvent?.id
            ? 'popover-mask'
            : 'popover-mask hide'
        }`}
        style={{ background: maskColor, zIndex }}
        onClick={closePopover}
      />

      {closeOnclick ? (
        <div
          onClick={closePopover}
          onKeyDown={closePopover}
          role="button"
          tabIndex="0"
          className={`${
            leftPosition && topPosition && reference === getEvent?.id
              ? 'popover-container'
              : 'popover-container popover-hide'
          } ${className}`}
          ref={popoverContainer}
          style={{
            left: leftPosition,
            top: topPosition,
            zIndex: zIndex + 1,
            width: setWidth,
            minWidth: setMinWidth,
          }}
        >
          {props.children}
        </div>
      ) : (
        <div
          className={`${
            (leftPosition && topPosition && reference === getEvent?.id) ||
            popoverOpen
              ? 'popover-container'
              : 'popover-container popover-hide'
          } ${className}`}
          ref={popoverContainer}
          style={{
            left: leftPosition,
            top: topPosition,
            zIndex: zIndex + 1,
            width: setWidth,
            minWidth: setMinWidth,
          }}
        >
          {props.children}
        </div>
      )}
    </>,
    document.body
  );
}

export default AdvancePopover;

AdvancePopover.propTypes = {
  placement: PropTypes.string,
  getEvent: PropTypes.objectOf(number),
  closePopover: PropTypes.func.isRequired,
  reference: PropTypes.string,
  closeOnclick: PropTypes.bool,
  children: PropTypes.node.isRequired,
  className: PropTypes.string,
  popoverOpen: PropTypes.bool,
  maskColor: PropTypes.string,
  zIndex: PropTypes.number,
  setWidth: PropTypes.string,
  setMinWidth: PropTypes.string,
};

AdvancePopover.defaultProps = {
  placement: '',
  reference: '',
  closeOnclick: false,
  getEvent: {},
  className: '',
  popoverOpen: false,
  maskColor: '',
  zIndex: 999999,
  setWidth: '',
  setMinWidth: '',
};
